import { Link } from 'react-router';
import { MessageSquare, Bot, TrendingUp, Clock, WalletIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useUserChats } from '@/api';

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function DashboardPage() {
  const { user } = useAuthStore();
  const { data: chatsData, isLoading: isLoadingChats } = useUserChats();

  const messageBalance = user?.remainingMessages ?? 0;
  const address = user?.publicKey
    ? `${user.publicKey.slice(0, 5)}...${user.publicKey.slice(-5)}`
    : null;

  const chats = chatsData?.data?.chats ?? [];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="mb-12">
        <h1 className="font-['Poppins',sans-serif] text-4xl font-semibold text-white mb-3">
          My Dashboard
        </h1>
        <p className="text-lg text-[#9595a5]">Track your activity and manage your account</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="p-6 rounded-xl bg-gradient-to-br from-[#18181f] to-[#1a1a22] border border-white/[0.08]">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div className="text-3xl font-bold text-white mb-1">{messageBalance}</div>
          <div className="text-sm text-[#9595a5]">Messages Available</div>
        </div>

        <div className="p-6 rounded-xl bg-gradient-to-br from-[#18181f] to-[#1a1a22] border border-white/[0.08]">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-1">{chats.length}</div>
          <div className="text-sm text-[#9595a5]">Active Chats</div>
        </div>

        <div className="p-6 rounded-xl bg-gradient-to-br from-[#18181f] to-[#1a1a22] border border-white/[0.08]">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center">
              <WalletIcon className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-1">{address ?? '—'}</div>
          <div className="text-sm text-[#9595a5]">Connected Wallet</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Chat History */}
        <div>
          <h2 className="font-['Poppins',sans-serif] text-2xl font-semibold text-white mb-6">
            Chat History
          </h2>
          <div className="space-y-4">
            {isLoadingChats ? (
              <div className="p-6 rounded-lg bg-[#18181f] border border-white/[0.08] text-center text-[#9595a5]">
                Loading chats...
              </div>
            ) : chats.length > 0 ? (
              chats.map((chat) => (
                <Link key={chat.chatId} to={`/chat/${chat.character._id}`}>
                  <div className="p-4 rounded-lg bg-[#18181f] border border-white/[0.08] hover:border-white/30 transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#2a2a35] flex items-center justify-center overflow-hidden">
                          {chat.character.imageUrl ? (
                            <img
                              src={chat.character.imageUrl}
                              alt={chat.character.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xl">🤖</span>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-white">{chat.character.name}</div>
                          <div className="text-sm text-[#9595a5]">
                            {chat.messageCount} messages
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#9595a5]">
                        <Clock className="w-4 h-4" />
                        {formatRelativeTime(chat.updatedAt)}
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="p-6 rounded-lg bg-[#18181f] border border-white/[0.08] text-center text-[#9595a5]">
                <p className="mb-2">No chats yet</p>
                <Link to="/explore">
                  <span className="text-white hover:underline">Start chatting</span>
                </Link>
              </div>
            )}
            <Link to="/explore">
              <button className="w-full py-3 rounded-lg border-2 border-dashed border-white/[0.08] text-[#9595a5] hover:border-white/30 hover:text-white transition-all">
                Browse More Bots
              </button>
            </Link>
          </div>
        </div>

        {/* Purchase */}
        <div>
          <h2 className="font-['Poppins',sans-serif] text-2xl font-semibold text-white mb-6">
            Messages
          </h2>
          <div className="p-6 rounded-xl bg-[#18181f] border border-white/[0.08] space-y-4">
            <div>
              <div className="text-2xl font-bold text-white mb-1">{messageBalance}</div>
              <div className="text-sm text-[#9595a5]">Available messages</div>
            </div>
            <Link to="/purchase">
              <button className="w-full py-3 rounded-lg bg-white text-black font-medium hover:opacity-90 transition-opacity">
                Buy More Messages
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Wallet Settings */}
      <div className="mt-12">
        <h2 className="font-['Poppins',sans-serif] text-2xl font-semibold text-white mb-6">
          Wallet & Settings
        </h2>
        <div className="p-6 rounded-xl bg-[#18181f] border border-white/[0.08]">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-white/[0.08]">
              <div>
                <div className="text-sm font-medium text-white mb-1">Connected Wallet</div>
                <div className="text-xs text-[#9595a5] font-mono">{address ?? '—'}</div>
              </div>
              <div className="px-3 py-1 rounded-md bg-white/20 text-white text-xs font-medium">
                Connected
              </div>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="text-sm font-medium text-white mb-1">Network</div>
                <div className="text-xs text-[#9595a5]">Solana</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
